import { Link, useLocation } from "wouter";
import { useState } from "react";
import { Menu, Sparkles, Home, BookOpen, HelpCircle, Info, DollarSign, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
// TODO: Uncomment when ready to enable dark mode
import { ThemeToggle } from "@/components/ui/theme-toggle";

export function PublicHeader() {
  const [location] = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const navLinks = [
    { href: "/", label: "Home", icon: Home },
    { href: "/how-it-works", label: "How It Works", icon: HelpCircle },
    { href: "/blog", label: "Blog", icon: BookOpen },
    { href: "/pricing", label: "Pricing", icon: DollarSign },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto flex h-16 md:h-20 items-center justify-between px-4">
        <div className="flex items-center gap-4">
          {/* Mobile Menu */}
          <div className="md:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid="button-mobile-menu"
                  aria-label="Open navigation menu"
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72">
                <div className="flex flex-col gap-6 mt-8">
                  <Link href="/">
                    <div className="flex items-center gap-2">
                      <img
                        src="/logos/logo.webp"
                        srcSet="/logos/logo-84w.webp 84w, /logos/logo-168w.webp 168w"
                        sizes="84px"
                        width="84"
                        height="84"
                        alt="PhotoshopRequest.app"
                        className="h-20 w-auto"
                      />
                      <span className="text-lg md:text-xl font-bold hidden sm:block text-primary">
                        PhotoshopRequest
                      </span>
                    </div>
                  </Link>

                  <nav className="flex flex-col gap-2">
                    {navLinks.map((link) => (
                      <Link key={link.href} href={link.href}>
                        <Button
                          variant={location === link.href ? "secondary" : "ghost"}
                          className="w-full justify-start gap-3 h-12"
                          onClick={() => setIsOpen(false)}
                          data-testid={`mobile-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          <link.icon className="h-5 w-5" />
                          {link.label}
                        </Button>
                      </Link>
                    ))}

                    <div className="border-t pt-4 mt-4">
                      <Link href="/dashboard">
                        <Button
                          className="w-full gap-2 h-12 bg-gradient-to-r from-primary to-primary/80"
                          onClick={() => setIsOpen(false)}
                          data-testid="mobile-link-dashboard"
                        >
                          Start Editing
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </nav>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Logo */}
          <Link href="/">
            <div className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity" data-testid="link-logo">
              <img
                src="/logos/logo.webp"
                srcSet="/logos/logo-84w.webp 84w, /logos/logo-168w.webp 168w"
                sizes="84px"
                width="84"
                height="84"
                alt="PhotoshopRequest.app"
                className="h-12 w-auto"
              />
              <span className="text-lg md:text-xl font-bold hidden sm:block text-primary">
                PhotoshopRequest
              </span>
            </div>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8 flex-1 justify-center">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={`text-sm font-medium transition-colors hover:text-primary cursor-pointer ${location === link.href
                ? "text-primary"
                : "text-muted-foreground"
                }`}
              data-testid={`link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* CTA & Theme Toggle */}
        <div className="flex items-center gap-3">
          {/* TODO: Uncomment when ready to enable dark mode */}
          <ThemeToggle />
          <Link href="/dashboard">
            <Button
              className="gap-2 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
              data-testid="button-dashboard"
            >
              <span className="hidden sm:inline">Start Editing</span>
              <span className="sm:hidden">Dashboard</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

